import { shortProgramTypeTitle, type FaIconTypes, type ProgramType } from "./constants";
import { tv, type VariantProps } from "tailwind-variants";
import { Activity, type Exercise } from "@/Common/ActivityFunctions";
import { capitalizeString, isActivityCompleted } from "@/Util/utils";

export const tagVariants = tv({
  base: "twp rounded-full inline-flex items-center text-center",
  variants: {
    variant: {
      default: "text-xerteal-600 bg-xerteal-600/20",
      bronze: "text-white bg-bronze-br",
      silver: "text-white bg-silver-br",
      gold: "text-white bg-gold-br",
      fake: "text-white bg-fake-br",
      workout: "text-[#5CA907] bg-[rgba(126,211,33,0.2)]",
      activity: "text-[#FF2222] bg-[rgba(255,0,0,0.2)]",
      session: "text-[#0B5DBC] bg-[rgba(74,144,226,0.2)]",
      forecast: "text-[#008d8f] bg-[linear-gradient(135deg,_#64dfdf80_0%,_#89c2d980_100%)]",
      forecastEvent: "bg-xerteal-200 text-xerteal-700",
      planned: "bg-[rgba(198,170,255,0.5)] text-[#7934bf]",
      race: "bg-[#ecd5fd] text-[#A01DE3]",
      rest: "bg-zinc-200 text-zinc-500",
      device: "bg-zinc-200 text-zinc-500",
      strava: "bg-zinc-200 text-zinc-500",
      completed: "text-[#6cba66] bg-[rgba(108,186,102,0.2)]",
      // User-applied tags -- see CustomActivityTags.svelte. `colored` sets no colours of its own:
      // the tag's colour goes inline (tagStyle()), and since Tailwind is configured `important`, any
      // bg/text class here would beat that inline style. `custom` is the neutral fallback for a tag
      // with no colour (e.g. a custom tag deleted since it was applied).
      colored: "",
      custom: "text-slate-700 bg-slate-200",
      button: "text-xerteal-600 bg-white hover:bg-slate-100 border border-slate-400 border-dashed",
    },
    size: {
      default: "px-3 py-1.5 text-sm leading-none",
      medium: "px-3 py-1.5 text-xs leading-none",
      small: "px-2 py-1 h-[22px] text-[10px] leading-none"
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

type Variant = VariantProps<typeof tagVariants>["variant"];
type Size = VariantProps<typeof tagVariants>["size"];

export interface Tag {
    label: string,
    variant: Variant,
    size: Size,
    faIcons?: string[], // This should be a fa-icon
    faIconTypes?: FaIconTypes[],
    tooltip?: string,
}

export function ActivityTag(tagSize: Size) {
    const size = tagSize

    function custom(label: string, variant: Variant = "default", faIcons: string[] = null, faIconTypes: FaIconTypes[] = null): Tag {
        return {
            label,
            variant,
            size,
            faIcons,
            faIconTypes,
        }
    }

    function list(labels: string[], variant: Variant = "default"): Tag[] {
        if (!labels) return []
        return labels.map((label) => {
            return {
                label,
                variant,
                size
            }
        })
    }

    function powerSource(exercise: Exercise): Tag | null {
        if(!Activity.isHeartrateDerived(exercise)) return null
        return {
            label: "Heartrate Derived",
            variant: "default",
            faIcons: ["fa-heart"],
            faIconTypes: ["fa-solid"],
            size
        }
    }

    function repeat(exercise: Exercise): Tag | null {
        let label: string
        if (Activity.repeatsDaily(exercise)) label = "Daily"
        else if (Activity.repeatsWeekly(exercise)) label = "Weekly"
        else return null
        return {
            label,
            variant: "default",
            size,
        }
    }

    function sharing (exercise: Exercise): Tag | null {
        if(!Activity.isPubliclyShared(exercise)) return null
        return {
            label: "Public",
            variant: "default",
            size,
        }
    }

    function pinned(exercise: Exercise): Tag | null {
        if(!Activity.isPinned(exercise)) return null
        return {
            label: "",
            variant: "forecast",
            faIcons: ["fa-thumbtack"],
            faIconTypes: ["fa-solid"],
            size,
        }
    }

    function planned(exercise: Exercise): Tag | null {
        if(!Activity.isPlanned(exercise)) return null
        return {
            label: "Planned",
            variant: "planned",
            size
        }
    }

    function manual(exercise: Exercise): Tag | null {
        if(!Activity.isManualEntry(exercise)) return null
        return {
            label: "Manual",
            variant: "default",
            size
        }
    }

    function race(exercise: Exercise): Tag | null {
        const label = Activity.getRaceTypeTag(exercise)
        if(!label) return null
        return {
            label,
            variant: "race",
            size,
        }
    }

    function forecast(exercise: Exercise, isRest: boolean = false): Tag | null {
        if(!Activity.isForecast(exercise)) return null
        const isSwapped = Activity.isSwapped(exercise);
        return {
            label: "Forecast",
            variant: isRest ? "rest" : "forecast",
            faIcons: isSwapped ? ["fa-swap"] : null,
            faIconTypes: isSwapped ? ["fa-solid"] : null,
            size,
            tooltip: isSwapped ? (new Date(exercise.original_placeholder_date)).toDateString() : null,
        }
    }

    function forecastEvent(exercise: Exercise, programType: ProgramType): Tag | null {
        if(!Activity.isGoalEvent(exercise) || !programType) return null
        return {
            label: shortProgramTypeTitle[programType],
            variant: "forecastEvent",
            size,
        }
    }
    
    function type(exercise: Exercise): Tag | null {
        const icon = Activity.getIcon(exercise)
        const type = Activity.getType(exercise)
        if(!type || !icon) return null
        const iconParts = icon.split(' ')
        return {
            label: type,
            variant: "default",
            faIcons: [iconParts[1]],
            faIconTypes: ["fa-light"],
            size
        }
    }

    function exerciseType(exercise: Exercise): Tag | null {
        const exerciseType = Activity.getExerciseType(exercise)  
        if (!exerciseType) return null;      
        return {
            label: exerciseType,
            variant: 'default',
            size,
        }
    }

    function breakthrough(exercise: Exercise, showFake = false): Tag | null {
        const medal = Activity.getBreakthroughMedal(exercise)
        if(!medal || (medal === "fake" && !showFake)) return null
        return {
            label: "Breakthrough",
            variant: medal,
            size,
        }
    }
    function device(exercise: Exercise): Tag | null {
        if (!exercise.data_source && !exercise.device_info?.manufacturer) return null
        let label: string
        if (exercise.data_source) {
            label = exercise.data_source
        } else {
            label = exercise.device_info.manufacturer + (exercise.device_info.product && exercise.device_info.product != 'unknown' ? ` ${exercise.device_info.product}` : "")
        }
        return {
            label, 
            variant: "device",
            size,
        }
    }
    function strava(exercise: Exercise): Tag | null {
        if (exercise.provider != 'strava') return null
        return {
            label: "Strava", 
            variant: "strava",
            size,
        }
    }

    function completed(exercise: Exercise): Tag | null {
    if (Activity.isPlanned(exercise)) return null;
        return {
            label: "Completed",
            variant: "completed",
            faIcons: ["fa-circle-check"],
            faIconTypes: ["fa-solid"],
            size,
        }
    }

    function rest(exercise: Exercise): Tag | null {
        if (!Activity.isForecast(exercise) || !Activity.isRest(exercise)) return null;
        return {
            label: "Rest",
            variant: "rest",
            size,
        }
    }

    function hidden(exercise: Exercise): Tag | null {
        if (!Activity.isHidden(exercise)) return null;
        return {
            label: "Hidden",
            variant: "default",
            faIcons: ["fa-eye-slash"],
            faIconTypes: ["fa-solid"],
            size,
        }
    }

    function duplicate(exercise: Exercise): Tag | null {
        if (!Activity.isDuplicate(exercise)) return null;
        return {
            label: "Duplicate",
            variant: "default",
            faIcons: ["fa-clone"],
            faIconTypes: ["fa-solid"],
            size,
        }
    }
    
    function groupSharing(exercise: Exercise): Tag | null {
        const sharingType = Activity.getGroupSharingType(exercise)
        if(!sharingType) return null
        return {
            label: capitalizeString(sharingType),
            variant: "default",
            size,
        }
    }

    function processedOnAlpha(exercise): Tag | null {
        if (!exercise.processedOnAlpha) return null;
        return {
            label: "alpha",
            variant: "default",
            faIcons: ["fa-flask"],
            faIconTypes: ["fa-solid"],
            size,
        }
    }

    function merged(exercise: Exercise): Tag | null {
        if(!Activity.isMerged(exercise)) return null
        return {
            label: "Merged",
            variant: "default",
            faIcons: ["fa-merge"],
            faIconTypes: ["fa-solid"],
            size,
        }
    }

    function group(exercise: Exercise): Tag | null {
        if(!exercise.group_members?.length) return null
        return {
            label: "Group",
            variant: "session",
            faIcons: ["fa-users"],
            faIconTypes: ["fa-solid"],
            size,
        }
    }

    return {
        custom,
        list,
        powerSource,
        repeat,
        sharing,
        pinned,
        planned,
        manual,
        race,
        forecast,
        forecastEvent,
        type,
        exerciseType,
        breakthrough,
        device,
        strava,
        completed,
        rest,
        hidden,
        duplicate,
        groupSharing,
        processedOnAlpha,
        merged,
        group
    }
}
